import axios from 'axios';
import { isJwtExpired, notifySessionExpired, SessionEndReason } from './session';

// Create an axios instance
const axiosInstance = axios.create({
  // You can set baseURL or other global config here
  // baseURL: 'https://api.example.com',
});

const authorizationOf = (config) =>
  config?.headers?.Authorization || config?.headers?.authorization;

// The API's codes for a table that is no longer open: 1020 (no open dining session, when
// reading it) and 1080 (it is closed, when ordering or paying). The session id comes from the
// guest's token, so neither means a wrong id.
const TABLE_CLOSED_CODES = [1020, 1080];
// 1005 on a 403: the request names another dining session than the token, so what this
// browser kept is stale.
const FORBIDDEN_ACCESS_CODE = 1005;
// A payment that brings the bill to zero closes the table, so on the payment's return and
// success screens a closed table is expected: the popup waits until the guest leaves them.
const PAYMENT_DONE_PATHS = ["/payment-return", "/payment-success"];
const onPaymentDoneScreen = () => PAYMENT_DONE_PATHS.some((p) => window.location.pathname.startsWith(p));

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Log the request config
    console.log('Request:', config);
    // An expired OPM token is never sent: the guest gets the session-expired popup instead.
    const auth = authorizationOf(config);
    if (auth && isJwtExpired(auth)) {
      notifySessionExpired();
      return Promise.reject(Object.assign(new Error('OPM session expired'), { sessionExpired: true }));
    }
    return config;
  },
  (error) => {
    // Handle request error
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Log the response
    console.log('Response:', response);
    return response;
  },
  (error) => {
    // Handle response error
    console.error('Response error:', error);
    // A request made with the guest's token was refused: the token is no longer valid, or the
    // table it was for has closed.
    if (authorizationOf(error.config)) {
      const status = error?.response?.status;
      const code = error?.response?.data?.internal_error_code;
      if (status === 401 || (status === 403 && code === FORBIDDEN_ACCESS_CODE)) {
        notifySessionExpired();
      } else if (TABLE_CLOSED_CODES.includes(code) && !onPaymentDoneScreen()) {
        notifySessionExpired(SessionEndReason.TABLE_CLOSED);
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
